import { subscribeComponentToErrorCode } from '@ferreri/minato-error-handler-react';
import { AxiosResponse } from 'axios';

import client from '../../../lib/service/plan.service';
import {
  PlanVersionsResponse,
  PlanVersion,
  PlanVersionResponse,
  PlanVersionCreateData,
  PlanVersionUpdateData,
  PlanVersionContractDocUpload,
  PlanVersionContractDocResponse,
  PlanVersionInsuranceContractDocResponse,
  PlanVersionInsuranceContractDocUpload,
  PlanVersionIsConfirmedEditParams,
} from './plan-version.type';
import { PlanVersionHasPendingDataError } from './not-confirmed/PlanVersionHasPendingDataError';

const PLAN_VERSIONS = 'v1/plan_versions';
const PLAN_VERSION = (id: string) => `${PLAN_VERSIONS}/${id}`;
const PLAN_VERSION_IS_CONFIRMED = (id: string) =>
  `${PLAN_VERSION(id)}/is_confirmed`;
const PLAN_VERSION_MANIFEST = (id: string) => `${PLAN_VERSION(id)}/manifest`;
const PLAN_VERSION_CONTRACT_DOC = (id: string) =>
  `${PLAN_VERSION(id)}/plan_version_document_contract`;
const PLAN_VERSION_INSURANCE_CONTRACT_DOC = (id: string) =>
  `${PLAN_VERSION(id)}/plan_version_document_insurance`;
const PLAN_VERSION_BY_BUSINESS_UNIT = (id: string) =>
  `v1/business_units/${id}/plan_versions`;
const PLAN_DISTRIBUTION_VERSIONS = (id: string) =>
  `v1/plan_distributions/${id}/plan_versions`;

export const getPlanVersion = async (id: string): Promise<PlanVersion> => {
  const response = await client.get<null, AxiosResponse<PlanVersionResponse>>(
    PLAN_VERSION(id),
  );
  return response.data.plan_version;
};

export const getPlanVersionDocumentContract = async (id: string) => {
  const response = await client.get(PLAN_VERSION_CONTRACT_DOC(id));
  return response.data.plan_version_document_contract;
};

export const getPlanVersionDocumentInsure = async (id: string) => {
  const response = await client.get(PLAN_VERSION_INSURANCE_CONTRACT_DOC(id));
  return response.data.plan_version_document_insurance;
};

export const getPlanVersionManifest = async (
  id: string,
): Promise<PlanVersion> => {
  const response = await client.get<null, AxiosResponse<PlanVersionResponse>>(
    PLAN_VERSION_MANIFEST(id),
  );

  return response.data.plan_version;
};

export const listPlanVersionsByBusinessUnit = async (
  id: string,
): Promise<PlanVersion[]> => {
  const response = await client.get<null, AxiosResponse<PlanVersionsResponse>>(
    PLAN_VERSION_BY_BUSINESS_UNIT(id),
    {
      params: {
        operational: true,
        is_corporate: false,
      },
    },
  );
  return response.data.plan_versions;
};

export const listPlanVersionsByPlanDistribution = async (
  id: string,
  status: string,
): Promise<PlanVersion[]> => {
  const response = await client.get<null, AxiosResponse<PlanVersionsResponse>>(
    PLAN_DISTRIBUTION_VERSIONS(id),
    {
      params: {
        status,
      },
    },
  );
  return response.data.plan_versions;
};

export const createPlanVersion = async (
  data: PlanVersionCreateData,
): Promise<PlanVersion> => {
  const response = await client.post(PLAN_VERSIONS, { plan_version: data });

  return response.data.plan_version;
};

export const updatePlanVersion = async (
  data: PlanVersionUpdateData,
): Promise<PlanVersion> => {
  const { id, ...rest } = data;
  const response = await client.put(PLAN_VERSION(id), {
    plan_version: rest,
  });

  return response.data.plan_version;
};

export const uploadContractDoc = async (
  id: string,
  file: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await client.post<
    PlanVersionContractDocUpload,
    AxiosResponse<PlanVersionContractDocResponse>
  >(PLAN_VERSION_CONTRACT_DOC(id), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.contract_document_id;
};

export const uploadInsuranceContractDoc = async (
  id: string,
  file: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await client.post<
    PlanVersionInsuranceContractDocUpload,
    AxiosResponse<PlanVersionInsuranceContractDocResponse>
  >(PLAN_VERSION_INSURANCE_CONTRACT_DOC(id), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.insurance_contract_document_id;
};

export const putPlanVersionIsConfirmed = async (
  params: PlanVersionIsConfirmedEditParams,
): Promise<PlanVersion> => {
  const response = await client.put(PLAN_VERSION_IS_CONFIRMED(params.id), {
    is_confirmed: params.is_confirmed,
  });

  return response.data.is_confirmed;
};

subscribeComponentToErrorCode(
  'PlanVersionHasPendingDataError',
  PlanVersionHasPendingDataError,
);

export const deleteContractDocument = async (id: string) => {
  await client.delete(PLAN_VERSION_CONTRACT_DOC(id));
};

export const deleteInsuranceDocument = async (id: string) => {
  await client.delete(PLAN_VERSION_INSURANCE_CONTRACT_DOC(id));
};

subscribeComponentToErrorCode(
  'PlanVersionDocumentInsuranceNotFoundError',
  () => null,
);

subscribeComponentToErrorCode(
  'PlanVersionDocumentContractNotFoundError',
  () => null,
);
